"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import { getUUID } from "@/lib/helpers/uuid";
import type { LoginSchema } from "@/schemas/auth-schema";
import type { UserType } from "@/types/auth";
import useUser from "./use-user";

interface LoginResponse {
	user: UserType;
	message?: string;
}

export default function useLogIn() {
	const { logIn: saveUserData } = useUser();
	const { replace } = useRouter();
	const [isMutating, setIsMutating] = useState(false);

	const logIn = async ({ email, password }: LoginSchema) => {
		setIsMutating(true);
		try {
			const response = await fetch("/api/auth/login", {
				method: "POST",
				headers: {
					"content-type": "application/json",
					"x-device-id": getUUID() || "",
				},
				body: JSON.stringify({ email, password }),
			});

			const data = (await response.json()) as LoginResponse & {
				message?: string;
			};

			if (!response.ok) {
				toast.error(data?.message || "Failed to log in");
				return;
			}

			if (data?.user) {
				saveUserData(data.user);
				toast.success("Successfully logged in!");
				replace("/");
			}
		} catch (error) {
			console.error("Failed to log in", error);
			toast.error("Failed to log in");
		} finally {
			setIsMutating(false);
		}
	};

	return { logIn, isMutating };
}
